import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, Monitor, PhoneCall, Plus, Smartphone } from 'lucide-react';
import { Alert, Badge, Button, Card, Input, Select, cn } from '../components/ui';
import { DEVICE_KINDS, DEVICE_LABELS, type DeviceKind } from '@klinkpbx/shared';
import { FormFooter, Grid, PageHeader, Row, Section, Tabs, Toggle } from '../components/form';
import { DataTable, type Column } from '../components/data-table';
import { DestinationPicker, type DestinationValue } from '../components/DestinationPicker';
import { ExtensionCredentials } from '../components/Credentials';
import { WebClientAccess } from '../components/WebClientAccess';
import { ApiError, api, type CreatedExtension, type Extension } from '../lib/api';

/**
 * The six presence states.
 *
 * The same set the user can dial with *91–*96 and the same set stored in the `presence_state`
 * enum. Ordered as a working day runs rather than alphabetically, because that is the order
 * somebody scans for the state they want.
 */
const PRESENCE = [
  { value: 'available', label: 'Available' },
  { value: 'away', label: 'Away' },
  { value: 'dnd', label: 'Do not disturb' },
  { value: 'lunch', label: 'Lunch break' },
  { value: 'business_trip', label: 'Business trip' },
  { value: 'off_work', label: 'Off work' },
] as const;

const presenceLabel = (value: string) => PRESENCE.find((p) => p.value === value)?.label ?? value;

const RECORDING = [
  { value: 'never', label: 'Do not record' },
  { value: 'always', label: 'Record every call' },
  { value: 'on_demand', label: 'On demand, from the phone or SDK' },
] as const;

const CODECS = [
  { value: 'ulaw', label: 'G.711 µ-law' },
  { value: 'alaw', label: 'G.711 A-law' },
  { value: 'g722', label: 'G.722 (HD)' },
  { value: 'opus', label: 'Opus' },
  { value: 'g729', label: 'G.729' },
] as const;

interface PresenceRule {
  presence: string;
  destinationType: string | null;
  destinationId: string | null;
  destinationValue: string | null;
  ringStrategy: 'ringall' | 'sequential' | null;
  ringTimeout: number | null;
}

// -------------------------------------------------------------------------------------------
// List
// -------------------------------------------------------------------------------------------

export function Extensions() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedExtension | null>(null);

  const { data: extensions = [], isLoading } = useQuery({
    queryKey: ['extensions'],
    queryFn: () => api.get<Extension[]>('/api/v1/extensions'),
  });

  // Polled rather than pushed: registration state changes on its own — a phone unplugged or a
  // laptop asleep produces no event here, it simply stops re-registering — so a list left open
  // has to re-ask. Cheap enough to do often: it is one grouped query over ps_contacts.
  const { data: online = {} } = useQuery({
    queryKey: ['extension-registrations'],
    queryFn: () => api.get<Record<string, Registration>>('/api/v1/extensions/registrations'),
    refetchInterval: 10_000,
  });

  const remove = useMutation({
    mutationFn: async (rows: readonly Extension[]) => {
      for (const row of rows) await api.del(`/api/v1/extensions/${row.id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['extensions'] }),
  });

  if (editing !== null) {
    return (
      <ExtensionEditor
        id={editing === 'new' ? null : editing}
        onCreated={setCreated}
        onClose={() => {
          setEditing(null);
          void queryClient.invalidateQueries({ queryKey: ['extensions'] });
        }}
      />
    );
  }

  const columns: ReadonlyArray<Column<Extension>> = [
    {
      key: 'status',
      header: 'Status',
      width: '9rem',
      render: (e) => <Presence state={online[e.number]} disabled={e.disabledAt !== null} />,
    },
    {
      key: 'number',
      header: 'Extension',
      width: '7rem',
      sort: (a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }),
      render: (e) => <span className="font-mono">{e.number}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (e) => e.name,
    },
    {
      key: 'email',
      header: 'Email',
      render: (e) => e.email ?? <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'devices',
      header: 'Type',
      width: '9rem',
      render: (e) => (
        <span className="flex gap-1.5">
          {e.sipEnabled ? <Badge>desk</Badge> : null}
          {e.webrtcEnabled ? <Badge>browser</Badge> : null}
          {!e.sipEnabled && !e.webrtcEnabled ? (
            <span className="text-muted-foreground">none</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'presence',
      header: 'Presence',
      width: '9rem',
      sort: (a, b) => a.presence.localeCompare(b.presence),
      render: (e) => (
        <span className={e.presence === 'available' ? '' : 'text-muted-foreground'}>
          {presenceLabel(e.presence)}
        </span>
      ),
    },
    {
      key: 'recording',
      header: 'Recording',
      width: '7rem',
      render: (e) =>
        e.recordingMode === 'never' ? (
          <span className="text-muted-foreground">Off</span>
        ) : (
          <Badge>{e.recordingMode === 'always' ? 'always' : 'on demand'}</Badge>
        ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Extensions"
        description="Each extension gets a desk-phone identity and a browser identity, with separate credentials."
      />

      {created ? <CredentialsPanel created={created} onDismiss={() => setCreated(null)} /> : null}

      <DataTable
        rows={extensions}
        columns={columns}
        rowKey={(e) => e.id}
        search={(e) => `${e.number} ${e.name} ${e.email ?? ''}`}
        searchPlaceholder="Search by number, name or email"
        toolbar={
          <Button onClick={() => setEditing('new')}>
            <Plus className="mr-1.5 size-4" />
            Add
          </Button>
        }
        onEdit={(e) => setEditing(e.id)}
        onDelete={(rows) => remove.mutate(rows)}
        deleteLabel={(rows) =>
          rows.length === 1
            ? `Delete extension ${rows[0]!.number}? Its number is released and its voicemail box is removed. To keep the number and the history, disable it instead.`
            : `Delete ${rows.length} extensions? Their numbers are released and their voicemail boxes are removed.`
        }
        empty={
          isLoading ? null : (
            <div className="py-10 text-center">
              <p className="font-medium">No extensions yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Add one to give somebody a phone number on this PBX.
              </p>
            </div>
          )
        }
      />
    </div>
  );
}

/**
 * Online state for one extension.
 *
 * Desk phone and browser are counted separately because they are separate endpoints, and the
 * difference matters when diagnosing: "the browser is connected but the desk phone is not" is a
 * different problem from the extension being offline entirely.
 */
interface RegisteredDevice {
  kind: DeviceKind;
  address: string | null;
  userAgent: string | null;
  expiresAt: string | null;
}

interface Registration {
  sip: number;
  webrtc: number;
  devices: RegisteredDevice[];
}

const DEVICE_ICON: Record<DeviceKind, typeof Monitor> = {
  desk: PhoneCall,
  desktop: Monitor,
  mobile: Smartphone,
  web: Globe,
};

/**
 * Which devices an extension is online from.
 *
 * One fixed slot per kind rather than one icon per registration, so the column stays the same
 * width on every row and can be scanned down. A ragged column of one-to-five icons is harder to
 * read at a glance than four slots where the lit ones stand out — and glancing down the column is
 * the entire purpose of a status column.
 *
 * The tooltip carries the address the device is actually reachable at, and the User-Agent it was
 * classified from, so a wrong guess about the kind is visibly a guess rather than a claim.
 */
function Presence({ state, disabled }: { state?: Registration | undefined; disabled: boolean }) {
  // Checked before registration: a disabled extension that still shows a stale contact would
  // read as working, which is the opposite of what disabling it was meant to achieve.
  if (disabled) {
    return (
      <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <span className="size-2 rounded-full bg-muted-foreground/40" />
        Disabled
      </span>
    );
  }

  const devices = state?.devices ?? [];

  if (devices.length === 0) {
    return (
      <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <span className="size-2 rounded-full bg-muted-foreground/40" />
        Offline
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1">
      {DEVICE_KINDS.map((kind) => {
        const here = devices.filter((d) => d.kind === kind);
        const Icon = DEVICE_ICON[kind];
        const online = here.length > 0;

        // Several contacts of one kind — two browser tabs, a desk phone re-registering — are one
        // slot with both addresses in the tooltip, not two slots that break the alignment.
        const tooltip = online
          ? here
              .map(
                (d) =>
                  `${DEVICE_LABELS[kind].toLowerCase()} / ${d.address ?? 'unknown address'}` +
                  (d.userAgent ? `\n${d.userAgent}` : ''),
              )
              .join('\n\n')
          : `No ${DEVICE_LABELS[kind].toLowerCase()} registered`;

        return (
          <span
            key={kind}
            title={tooltip}
            aria-label={tooltip}
            className={cn(
              'inline-flex size-6 items-center justify-center rounded',
              online ? 'bg-primary/10 text-primary' : 'text-muted-foreground/25',
            )}
          >
            <Icon className="size-3.5" />
            {here.length > 1 && (
              <span className="ml-0.5 text-[10px] font-medium tabular-nums">{here.length}</span>
            )}
          </span>
        );
      })}
    </span>
  );
}

// -------------------------------------------------------------------------------------------
// Editor
// -------------------------------------------------------------------------------------------

interface Draft {
  number: string;
  name: string;
  email: string;
  language: string;
  ringTimeout: number;
  callLimit: number;
  disabled: boolean;
  sipEnabled: boolean;
  webrtcEnabled: boolean;
  voicemailEnabled: boolean;
  recordingMode: string;
  outboundCidNumber: string;
  outboundCidName: string;
  codecs: string[];
  presence: string;
  cfAlways: string;
  cfBusy: string;
  cfNoAnswer: string;
  pickupGroups: string;
}

const BLANK: Draft = {
  number: '',
  name: '',
  email: '',
  language: 'en',
  ringTimeout: 30,
  callLimit: 5,
  disabled: false,
  sipEnabled: true,
  webrtcEnabled: true,
  voicemailEnabled: true,
  recordingMode: 'never',
  outboundCidNumber: '',
  outboundCidName: '',
  codecs: [],
  presence: 'available',
  cfAlways: '',
  cfBusy: '',
  cfNoAnswer: '',
  pickupGroups: '',
};

function toDraft(e: Extension): Draft {
  return {
    number: e.number,
    name: e.name,
    email: e.email ?? '',
    language: e.language,
    ringTimeout: e.ringTimeout,
    callLimit: e.callLimit,
    disabled: e.disabledAt !== null,
    sipEnabled: e.sipEnabled,
    webrtcEnabled: e.webrtcEnabled,
    voicemailEnabled: e.voicemailEnabled,
    recordingMode: e.recordingMode,
    outboundCidNumber: e.outboundCidNumber ?? '',
    outboundCidName: e.outboundCidName ?? '',
    codecs: e.codecs ?? [],
    presence: e.presence,
    cfAlways: e.cfAlways ?? '',
    cfBusy: e.cfBusy ?? '',
    cfNoAnswer: e.cfNoAnswer ?? '',
    pickupGroups: (e.pickupGroups ?? []).join(', '),
  };
}

/**
 * The payload for a save.
 *
 * Blank optional strings are sent as null rather than omitted, so clearing a field in the form
 * actually clears it. Omitting would leave the stored value untouched, which is the behaviour
 * that makes a form feel like it ignored you.
 */
function toPayload(draft: Draft, creating: boolean) {
  const base = {
    name: draft.name,
    language: draft.language,
    ringTimeout: draft.ringTimeout,
    callLimit: draft.callLimit,
    sipEnabled: draft.sipEnabled,
    webrtcEnabled: draft.webrtcEnabled,
    voicemailEnabled: draft.voicemailEnabled,
    recordingMode: draft.recordingMode,
    codecs: draft.codecs,
    presence: draft.presence,
    cfAlways: draft.cfAlways.trim() || null,
    cfBusy: draft.cfBusy.trim() || null,
    cfNoAnswer: draft.cfNoAnswer.trim() || null,
    pickupGroups: draft.pickupGroups
      .split(',')
      .map((g) => g.trim())
      .filter(Boolean),
    // The API validates these as bounded INI values, which reject an empty string — so an unset
    // caller id has to be omitted entirely rather than sent blank.
    ...(draft.email.trim() ? { email: draft.email.trim() } : {}),
    ...(draft.outboundCidNumber.trim()
      ? { outboundCidNumber: draft.outboundCidNumber.trim() }
      : {}),
    ...(draft.outboundCidName.trim() ? { outboundCidName: draft.outboundCidName.trim() } : {}),
  };

  return creating ? { ...base, number: draft.number } : { ...base, disabled: draft.disabled };
}

function ExtensionEditor({
  id,
  onClose,
  onCreated,
}: {
  id: string | null;
  onClose: () => void;
  onCreated: (created: CreatedExtension) => void;
}) {
  const creating = id === null;
  const [tab, setTab] = useState('basic');
  const [draft, setDraft] = useState<Draft>(BLANK);
  const [rules, setRules] = useState<PresenceRule[]>([]);

  const { data: existing } = useQuery({
    queryKey: ['extension', id],
    queryFn: () => api.get<Extension>(`/api/v1/extensions/${id}`),
    enabled: !creating,
  });

  const { data: storedRules } = useQuery({
    queryKey: ['presence-rules', id],
    queryFn: () => api.get<PresenceRule[]>(`/api/v1/extensions/${id}/presence-rules`),
    enabled: !creating,
  });

  useEffect(() => {
    if (existing) setDraft(toDraft(existing));
  }, [existing]);

  useEffect(() => {
    if (storedRules) setRules(storedRules);
  }, [storedRules]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const save = useMutation({
    mutationFn: async () => {
      if (creating) {
        return api.post<CreatedExtension>('/api/v1/extensions', toPayload(draft, true));
      }
      await api.patch(`/api/v1/extensions/${id}`, toPayload(draft, false));
      // Saved as part of the same action, because to an admin this is one form. Sequential
      // rather than parallel so a rejected patch does not leave rules applied to a row that
      // failed to change.
      await api.put(`/api/v1/extensions/${id}/presence-rules`, { rules });
      return null;
    },
    onSuccess: (result) => {
      if (result) onCreated(result);
      onClose();
    },
  });

  const conflict = save.error instanceof ApiError && save.error.isConflict;

  const tabs = [
    { id: 'basic', label: 'Basic' },
    { id: 'presence', label: 'Presence' },
    { id: 'features', label: 'Features' },
    { id: 'advanced', label: 'Advanced' },
    ...(creating ? [] : [{ id: 'client', label: 'Web client' }]),
    ...(creating ? [] : [{ id: 'credentials', label: 'Credentials' }]),
  ];

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <PageHeader
        title={creating ? 'Add extension' : `Extension ${draft.number}`}
        description={
          creating
            ? 'Credentials are generated on save and shown once.'
            : draft.name || 'Settings for one extension.'
        }
      />

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {tab === 'basic' && (
        <Section
          title="Identity"
          description="Who this extension belongs to and how it is dialled."
        >
          <Grid>
            <Row
              label="Extension number"
              required={creating}
              error={conflict ? 'That number is already in use' : undefined}
              hint={
                creating
                  ? '2–11 digits.'
                  : 'The number cannot be changed. It is the key the call history, voicemail box and generated dialplan all point at — renumbering is a migration, not an edit.'
              }
            >
              <Input
                value={draft.number}
                onChange={(e) => set('number', e.target.value)}
                placeholder="1001"
                inputMode="numeric"
                required={creating}
                disabled={!creating}
                className={creating ? undefined : 'font-mono'}
              />
            </Row>

            <Row label="Name" required hint="Shown as the caller ID on internal calls.">
              <Input
                value={draft.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="Alice Chen"
                required
              />
            </Row>

            <Row label="Email" hint="Where voicemail notifications go.">
              <Input
                type="email"
                value={draft.email}
                onChange={(e) => set('email', e.target.value)}
                placeholder="alice@example.com"
              />
            </Row>

            <Row label="Prompt language" hint="Which language the system prompts are played in.">
              <Select value={draft.language} onChange={(v) => set('language', v)}>
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
                <option value="it">Italian</option>
                <option value="th">Thai</option>
              </Select>
            </Row>

            {!creating && (
              <Row
                label="Status"
                span
                hint="A disabled extension keeps its number and its call history, but cannot register, sign in to the web client, or be provisioned. This is what you want when somebody leaves."
              >
                <div className="flex h-9 items-center gap-3">
                  <Toggle
                    checked={!draft.disabled}
                    onChange={(v) => set('disabled', !v)}
                    label="Extension status"
                  />
                  <span className="text-sm text-muted-foreground">
                    {draft.disabled ? 'Disabled' : 'Enabled'}
                  </span>
                </div>
              </Row>
            )}
          </Grid>
        </Section>
      )}

      {tab === 'presence' && (
        <div className="space-y-5">
          <Section
            title="Current status"
            description="The user can change this themselves from their handset with *91–*96, or from the web client."
          >
            <Grid>
              <Row label="Presence">
                <Select value={draft.presence} onChange={(v) => set('presence', v)}>
                  {PRESENCE.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </Select>
              </Row>
            </Grid>
          </Section>

          <Section
            title="What happens in each status"
            description="Set once, and calls follow the person as their status changes through the day. A status set to ring as normal behaves as though no rule existed."
          >
            <div className="space-y-4">
              {PRESENCE.map((p) => (
                <PresenceRuleRow
                  key={p.value}
                  label={p.label}
                  active={draft.presence === p.value}
                  rule={rules.find((r) => r.presence === p.value)}
                  extensionRingTimeout={draft.ringTimeout}
                  onChange={(next) =>
                    setRules((current) => [
                      ...current.filter((r) => r.presence !== p.value),
                      { ...next, presence: p.value },
                    ])
                  }
                />
              ))}
            </div>

            {/*
              Stated because it is the first question somebody asks when both are set, and the
              answer is not obvious from either screen on its own.
            */}
            <p className="mt-4 text-xs text-muted-foreground">
              A forward set by the user with *31 takes priority over these rules. Somebody who
              dialled a forwarding code has expressed a specific intent, and a status rule quietly
              overriding it is how a PBX starts to feel haunted.
            </p>
          </Section>
        </div>
      )}

      {tab === 'features' && (
        <div className="space-y-5">
          <Section title="Voicemail and recording">
            <Grid>
              <Row label="Voicemail" hint="Unanswered calls reach a mailbox for this extension.">
                <div className="flex h-9 items-center gap-3">
                  <Toggle
                    checked={draft.voicemailEnabled}
                    onChange={(v) => set('voicemailEnabled', v)}
                    label="Voicemail"
                  />
                  <span className="text-sm text-muted-foreground">
                    {draft.voicemailEnabled ? 'On' : 'Off'}
                  </span>
                </div>
              </Row>

              <Row
                label="Call recording"
                hint="Recordings are kept on this server and listed under Call history."
              >
                <Select value={draft.recordingMode} onChange={(v) => set('recordingMode', v)}>
                  {RECORDING.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </Select>
              </Row>
            </Grid>
          </Section>

          <Section
            title="Forwarding"
            description="Applies whatever the presence status is. Leave blank for none."
          >
            <Grid>
              <Row
                label="Forward all calls to"
                hint="An extension or an external number. Nothing rings first."
              >
                <Input
                  value={draft.cfAlways}
                  onChange={(e) => set('cfAlways', e.target.value)}
                  placeholder="1002"
                />
              </Row>
              <Row label="Forward when busy to">
                <Input value={draft.cfBusy} onChange={(e) => set('cfBusy', e.target.value)} />
              </Row>
              <Row
                label="Forward on no answer to"
                hint={`Fires after the ring timeout of ${draft.ringTimeout} seconds.`}
              >
                <Input
                  value={draft.cfNoAnswer}
                  onChange={(e) => set('cfNoAnswer', e.target.value)}
                />
              </Row>
              <Row
                label="Pickup groups"
                hint="Comma separated. Colleagues in the same group can answer each other's phones with *8."
              >
                <Input
                  value={draft.pickupGroups}
                  onChange={(e) => set('pickupGroups', e.target.value)}
                  placeholder="sales, reception"
                />
              </Row>
            </Grid>
          </Section>

          <Section
            title="Outbound caller ID"
            description="What the far end sees on external calls. Most carriers only allow numbers you own, and silently substitute their own otherwise."
          >
            <Grid>
              <Row label="Number">
                <Input
                  value={draft.outboundCidNumber}
                  onChange={(e) => set('outboundCidNumber', e.target.value)}
                  placeholder="+442071234567"
                />
              </Row>
              <Row label="Name">
                <Input
                  value={draft.outboundCidName}
                  onChange={(e) => set('outboundCidName', e.target.value)}
                  placeholder="Acme Ltd"
                />
              </Row>
            </Grid>
          </Section>
        </div>
      )}

      {tab === 'advanced' && (
        <Section
          title="Calls and codecs"
          description="Leave these alone unless a phone or a carrier is misbehaving."
        >
          <Grid>
            <Row
              label="Ring timeout"
              hint="Seconds before an unanswered call gives up and follows the no-answer rule."
            >
              <Input
                type="number"
                min={5}
                max={300}
                value={draft.ringTimeout}
                onChange={(e) => set('ringTimeout', Number(e.target.value))}
              />
            </Row>

            <Row
              label="Concurrent calls"
              hint="How many calls this extension can hold at once, counting held ones."
            >
              <Input
                type="number"
                min={1}
                max={50}
                value={draft.callLimit}
                onChange={(e) => set('callLimit', Number(e.target.value))}
              />
            </Row>

            <Row
              label="Devices"
              span
              hint="Two separate identities with separate credentials. Turning the browser identity off does not disturb a working desk phone."
            >
              <div className="flex flex-wrap items-center gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <Toggle
                    checked={draft.sipEnabled}
                    onChange={(v) => set('sipEnabled', v)}
                    label="Desk phone"
                  />
                  Desk phone
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Toggle
                    checked={draft.webrtcEnabled}
                    onChange={(v) => set('webrtcEnabled', v)}
                    label="Browser"
                  />
                  Browser and Web SDK
                </label>
              </div>
            </Row>

            <Row
              label="Codecs"
              span
              hint="Leave all unticked to use the system default. Order is not significant here — Asterisk negotiates from what both ends offer."
            >
              <div className="flex flex-wrap gap-4">
                {CODECS.map((c) => (
                  <label key={c.value} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="size-4 accent-[--primary]"
                      checked={draft.codecs.includes(c.value)}
                      onChange={(e) =>
                        set(
                          'codecs',
                          e.target.checked
                            ? [...draft.codecs, c.value]
                            : draft.codecs.filter((x) => x !== c.value),
                        )
                      }
                    />
                    {c.label}
                  </label>
                ))}
              </div>
            </Row>
          </Grid>
        </Section>
      )}

      {tab === 'client' && id && (
        <div className="space-y-5">
          <WebClientAccess id={id} />
        </div>
      )}

      {tab === 'credentials' && id && (
        <div className="space-y-5">
          <ExtensionCredentials id={id} />
        </div>
      )}

      {save.error && !conflict ? <Alert>{(save.error as Error).message}</Alert> : null}

      <FormFooter
        onCancel={onClose}
        saving={save.isPending}
        saveLabel={creating ? 'Create' : 'Save'}
      />
    </form>
  );
}

/**
 * One presence state and where its calls go.
 *
 * The choice is deliberately two steps — ring as normal, or pick a destination — rather than a
 * picker with a "none" entry. "Ring as normal" is the common case and the one somebody returns to
 * when turning a rule off, so it should not be buried in a list of call flows.
 */
function PresenceRuleRow({
  label,
  active,
  rule,
  extensionRingTimeout,
  onChange,
}: {
  label: string;
  active: boolean;
  rule: PresenceRule | undefined;
  extensionRingTimeout: number;
  onChange: (next: Omit<PresenceRule, 'presence'>) => void;
}) {
  const current: Omit<PresenceRule, 'presence'> = {
    destinationType: rule?.destinationType ?? null,
    destinationId: rule?.destinationId ?? null,
    destinationValue: rule?.destinationValue ?? null,
    ringStrategy: rule?.ringStrategy ?? null,
    ringTimeout: rule?.ringTimeout ?? null,
  };

  const forwarding = current.destinationType != null;

  return (
    <div className="rounded-[--radius] border border-border p-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="min-w-32 text-sm font-medium">
          {label}
          {active && (
            <Badge className="ml-2" tone="success">
              current
            </Badge>
          )}
        </span>

        <Select
          value={forwarding ? 'forward' : 'ring'}
          onChange={(v) =>
            onChange({
              ...current,
              ...(v === 'ring'
                ? { destinationType: null, destinationId: null, destinationValue: null }
                : { destinationType: 'voicemail', destinationId: null, destinationValue: null }),
            })
          }
        >
          <option value="ring">Ring as normal</option>
          <option value="forward">Send calls to…</option>
        </Select>
      </div>

      {forwarding ? (
        <div className="mt-3">
          <DestinationPicker
            label="Destination"
            value={
              {
                destinationType: current.destinationType,
                destinationId: current.destinationId,
                destinationValue: current.destinationValue,
              } as DestinationValue
            }
            onChange={(next) => onChange({ ...current, ...next })}
          />
        </div>
      ) : (
        /*
          Only shown when the state actually rings. A state that forwards before anything rings
          has no ring behaviour to configure, and offering it there would be a control that
          quietly does nothing.
        */
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <Row
            label="Ring devices"
            hint="In order rings one phone at a time, in the order they registered."
          >
            <Select
              value={current.ringStrategy ?? 'default'}
              onChange={(v) =>
                onChange({
                  ...current,
                  ringStrategy: v === 'default' ? null : (v as 'ringall' | 'sequential'),
                })
              }
            >
              <option value="default">Use the extension setting (all at once)</option>
              <option value="ringall">All at once</option>
              <option value="sequential">In order, one at a time</option>
            </Select>
          </Row>

          <Row
            label="Ring timeout"
            hint={
              current.ringStrategy === 'sequential'
                ? `Seconds per device — with several phones a caller waits this long for each one.`
                : `Seconds before the no-answer rule applies. Blank uses the extension setting of ${extensionRingTimeout}.`
            }
          >
            <Input
              type="number"
              min={5}
              max={300}
              value={current.ringTimeout ?? ''}
              placeholder={String(extensionRingTimeout)}
              onChange={(e) =>
                onChange({
                  ...current,
                  ringTimeout: e.target.value === '' ? null : Number(e.target.value),
                })
              }
            />
          </Row>
        </div>
      )}
    </div>
  );
}

/**
 * Credentials are shown exactly once.
 *
 * The server stores only an HA1 digest plus an encrypted copy for provisioning, and there is no
 * endpoint to read them back. That is deliberate, so the UI has to be emphatic about it — an
 * admin who closes this panel without copying has to rotate the secret.
 */
function CredentialsPanel({
  created,
  onDismiss,
}: {
  created: CreatedExtension;
  onDismiss: () => void;
}) {
  return (
    <Card className="space-y-4 border-warning/40 bg-warning/5">
      <div>
        <h2 className="font-medium">Extension {created.number} created</h2>
        <p className="mt-1 text-sm text-warning">
          These credentials are shown once and cannot be retrieved. Copy them now.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {created.credentials.sip ? (
          <CredentialBlock title="Desk phone" credential={created.credentials.sip} />
        ) : null}
        {created.credentials.webrtc ? (
          <CredentialBlock title="Browser / Web SDK" credential={created.credentials.webrtc} />
        ) : null}
      </div>

      <Button variant="secondary" onClick={onDismiss}>
        I have copied them
      </Button>
    </Card>
  );
}

function CredentialBlock({
  title,
  credential,
}: {
  title: string;
  credential: { username: string; password: string };
}) {
  return (
    <div className="rounded-[--radius] border border-border bg-background p-3">
      <p className="text-xs font-medium text-muted-foreground">{title}</p>
      <dl className="mt-2 space-y-1 font-mono text-sm">
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">user</dt>
          <dd className="break-all">{credential.username}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">pass</dt>
          <dd className="break-all">{credential.password}</dd>
        </div>
      </dl>
    </div>
  );
}
